import { useQueryClient } from '@tanstack/react-query';
import type { Cenario } from '@favo/core';
import * as React from 'react';

import type { Analise, Diagnostico } from '@/dados';
import { api, chaves, mensagemDoErro } from '@/dados';

/** Diagnóstico deste projeto gravado há menos que isto: F5 não grava outro. */
const JANELA_ARQUIVAMENTO_MS = 10 * 60 * 1000;

/**
 * Grava o snapshot do laudo em `diagnosticos`. Duas travas contra duplicata:
 * a referência local cobre a visita (re-render não grava de novo) e a lista de
 * diagnósticos cobre o F5, que zera a referência mas não o banco.
 *
 * Falha nunca é silêncio: o rodapé avisa. E não arquiva laudo sem diagnóstico
 * (estado sem preço), que seria linha de banco sem conteúdo.
 *
 * A escrita usa `api.criar` porque `dados/` só expõe a LEITURA de diagnósticos
 * (`useDiagnosticos`); o par de escrita, um `useSalvarDiagnostico` simétrico ao
 * `useSalvarOrcamento`, ainda não existe e esta tela não pode criá-lo.
 */
export function useArquivamento({
  analise,
  emitidoEm,
  arquivados,
  arquivadosCarregando,
}: {
  analise: Analise;
  emitidoEm: string;
  arquivados: Diagnostico[] | undefined;
  arquivadosCarregando: boolean;
}): string | null {
  const clienteQuery = useQueryClient();
  const jaTentou = React.useRef(false);
  const [erro, setErro] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (jaTentou.current || arquivadosCarregando) return;
    if (!analise.cenarioAtual) return;

    const corte = Date.now() - JANELA_ARQUIVAMENTO_MS;
    const recente = (arquivados ?? []).some(
      (item) => item.projeto_id === analise.projeto_id && new Date(item.criado_em).getTime() > corte,
    );

    jaTentou.current = true;
    if (recente) return;

    let ativo = true;
    void api
      .criar<Diagnostico>('/diagnosticos', {
        projeto_id: analise.projeto_id,
        snapshot: montarSnapshot(analise, emitidoEm),
      })
      .then(() => {
        if (ativo) void clienteQuery.invalidateQueries({ queryKey: chaves.diagnosticos });
      })
      .catch((causa: unknown) => {
        if (ativo) setErro(mensagemDoErro(causa));
      });

    return () => {
      ativo = false;
    };
  }, [analise, emitidoEm, arquivados, arquivadosCarregando, clienteQuery]);

  return erro;
}

function montarSnapshot(analise: Analise, emitidoEm: string): Record<string, unknown> {
  const p = analise.parametros;
  const rec = analise.recomendacao;

  const resumo = (cen: Cenario | null) =>
    cen === null
      ? null
      : {
          ticket: cen.ticket,
          mcUnit: cen.mcUnit,
          mcPct: cen.mcPct,
          lucroUnit: cen.lucroUnit,
          lucroPct: cen.lucroPct,
          anual: cen.anual,
        };

  return {
    gerado_em: emitidoEm,
    estado: rec.estado,
    // Nome deliberado: é o PISO para a meta, não uma sugestão de preço. Quem
    // ler este arquivo amanhã não tem o tipo do motor para lembrá-lo disso.
    preco_minimo_para_meta: 'precoSugerido' in rec ? rec.precoSugerido : null,
    parametros: {
      ticket: p.ticket,
      ticketOrigem: p.ticketOrigem,
      cdt: p.cdt,
      kit: p.kit,
      comissao: p.comissao,
      comissaoBase: p.comissaoBase,
      tributo: p.tributo,
      tributoBase: p.tributoBase,
      outras: p.outras,
      despesasExtras: p.despesasExtras,
      despFixa: p.despFixa,
      lucroAlvo: p.lucroAlvo,
      lucroAlvoDefinido: p.lucroAlvoDefinido,
      V12: p.V12,
      coberturaSaz: p.coberturaSaz,
      temSazonalidade: p.temSazonalidade,
      kwp: p.kwp,
      mods: p.mods,
    },
    cenarios: {
      atual: resumo(analise.cenarioAtual),
      no_minimo: rec.estado === 'abaixo' ? resumo(rec.sugerido) : null,
    },
    distribuicao: analise.distribuicao
      ? {
          mesEquilibrio: analise.distribuicao.mesEquilibrio,
          fixasAno: analise.distribuicao.fixasAno,
          uniforme: analise.distribuicao.uniforme,
        }
      : null,
  };
}
